import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionDetailInfo } from './transactionDetail.types';
import { useClientData } from '@/providers/ClientData.provider';
import {
  Box,
  Card,
  formattedCurrency,
  InteracTransaction,
  withStyles,
  capitalizeFirstLetter,
  InteracWordmark,
  TransactionType,
} from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus, UtilButton } from 'UI';
import ApprovalHistoryDetailsCard from './Card/ApprovalHistoryDetailsCard';
import styles from '../Transactions.module.scss';
import useElinxDetails from './hooks/useElinxDetails';

const cn = withStyles(styles);

type InteracMoneyRequestTransactionInfoProps = {
  transaction: InteracTransaction;
  Notes: string;
  isParent?: boolean;
  jwt: string;
  ELinxRequestID?: string;
  transactionDetail: TransactionDetailInfo;
};

const InteracMoneyRequestTransactionInfo: FC<InteracMoneyRequestTransactionInfoProps> = ({
  transaction,
  Notes,
  isParent = false,
  jwt,
  ELinxRequestID = '',
  transactionDetail,
}) => {
  const { accountId } = transactionDetail;

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.InteracMoneyRequestTransactionInfo',
  });

  const { t: tGlobal } = useTranslation('translations');

  const {
    response: { clientAccountsEnabled, JWT: mainJwt },
  } = useClientData();

  const isMainAccount = !jwt || jwt === mainJwt;

  const { elinxAdditionalId, invoiceButton } = useElinxDetails({ ELinxRequestID, jwt });

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionStatus', { defaultValue: 'Transaction Details' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                defaultValue: transaction.TransactionStatus,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: <InteracWordmark type="moneyRequest" />,
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: transaction.Currency + ' ' + formattedCurrency(transaction.Amount),
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.Currency + ' ' + formattedCurrency(transaction.HoldAmount),
    },
    {
      label: t('ETransferDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('ETransferDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: Notes || '-',
    },
    {
      label: t('TransactionDetails.invoice', { defaultValue: 'Invoice' }),
      value: invoiceButton,
      visible: !!invoiceButton,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: transaction.FailureReason || '-',
      visible: isParent && transaction.FailureReason !== '',
    },
  ];

  const eTransferDetails = [
    {
      label: t('ETransferDetails.recipientName', { defaultValue: 'Recipient Name' }),
      value:
        accountId && accountId !== '' && isMainAccount ? (
          <UtilButton
            onClick={() => {
              if (clientAccountsEnabled) {
                window.open(`/client-accounts/${accountId}`, '_blank');
              } else {
                window.open(`/contacts/${accountId}`, '_blank');
              }
            }}
          >
            {transaction.RecipientName}
          </UtilButton>
        ) : (
          transaction.RecipientName
        ),
    },
    {
      label: t('ETransferDetails.email', { defaultValue: 'Email' }),
      value: transaction.Email,
      visible: transaction.Email !== '',
    },
    {
      label: t('ETransferDetails.phone', { defaultValue: 'Phone' }),
      value: transaction.PhoneNumber,
      visible: transaction.PhoneNumber !== '',
    },
    {
      label: t('ETransferDetails.messageForRecipient', { defaultValue: 'Message For Recipient' }),
      value: transaction.MessageForRecipient || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: transaction.FailureReason || '-',
      visible: !isParent,
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      {!isParent && (
        <TransactionCard
          receiverName={transaction.RecipientName}
          amount={transaction.Amount}
          currency={transaction.Currency}
          transactionId={transaction.TransactionID}
          type={'Interac Money Request'}
          failureReason={transaction.FailureReason}
          contactId={accountId}
          jwt={jwt}
          isMainAccount={isMainAccount}
          additionalId={elinxAdditionalId}
        />
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>

        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <ApprovalHistoryDetailsCard
        transactionID={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={TransactionType.INTERACT_MONEY_REQUEST}
        jwt={jwt}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          <InteracWordmark type="moneyRequest" /> {t('eTransferDetailsTitle', { defaultValue: 'Details' })}
        </Title>

        <DetailsRows className={cn('rows')} details={eTransferDetails} />
      </Card>

      {transaction.FailureReason && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>

          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default InteracMoneyRequestTransactionInfo;
